import { Boxes, FolderKanban, ListChecks, Settings as SettingsIcon, Wallet } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";

/**
 * Signature component — global cmd-K command palette.
 *
 * Linear-style two-key navigation:
 *   g p  → Projects
 *   g r  → Runs
 *   g b  → Budget
 *   g a  → Artifacts
 *   g s  → Settings
 *   /    → focus search (opens palette)
 *   ?    → shortcut help (opens palette to ? group)
 *
 * The presence of cmd-K is a single bit of signal that says "this
 * is a real product." Power users find it within a session; pilot
 * scientists love it; it costs ~5KB.
 */
export function RunCommandPalette(): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  // Track the leading "g" of a "g <key>" two-key chord with a short
  // timeout so an unrelated key press doesn't get treated as the
  // second key in the chord.
  const gPressedAt = React.useRef<number | null>(null);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Cmd-K / Ctrl-K: open palette.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((v) => !v);
        return;
      }

      // Ignore keys typed into inputs / textareas.
      const target = event.target as HTMLElement | null;
      const isEditable =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isEditable) return;

      const now = Date.now();

      // "g <key>" chord: only fire if the previous "g" was within 800ms.
      const isWithinChord = gPressedAt.current !== null && now - gPressedAt.current < 800;

      if (event.key === "g" && !isWithinChord) {
        gPressedAt.current = now;
        return;
      }

      if (isWithinChord) {
        gPressedAt.current = null;
        switch (event.key) {
          case "p":
            event.preventDefault();
            navigate("/projects");
            return;
          case "r":
            event.preventDefault();
            navigate("/runs");
            return;
          case "b":
            event.preventDefault();
            navigate("/budget");
            return;
          case "a":
            event.preventDefault();
            navigate("/artifacts");
            return;
          case "s":
            event.preventDefault();
            navigate("/settings");
            return;
          default:
            return;
        }
      }

      // "/" → open palette focused on search.
      if (event.key === "/") {
        event.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  const go = React.useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate]
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command, search for a project, run, artifact…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/projects")}>
            <FolderKanban className="text-muted-foreground" />
            <span>Projects</span>
            <CommandShortcut>G P</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/runs")}>
            <ListChecks className="text-muted-foreground" />
            <span>Runs</span>
            <CommandShortcut>G R</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/artifacts")}>
            <Boxes className="text-muted-foreground" />
            <span>Artifacts</span>
            <CommandShortcut>G A</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/budget")}>
            <Wallet className="text-muted-foreground" />
            <span>Budget</span>
            <CommandShortcut>G B</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go("/settings")}>
            <SettingsIcon className="text-muted-foreground" />
            <span>Settings</span>
            <CommandShortcut>G S</CommandShortcut>
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Shortcuts">
          <CommandItem disabled>
            <span>Open command palette</span>
            <CommandShortcut>⌘K</CommandShortcut>
          </CommandItem>
          <CommandItem disabled>
            <span>Focus search</span>
            <CommandShortcut>/</CommandShortcut>
          </CommandItem>
          <CommandItem disabled>
            <span>Two-key navigation</span>
            <CommandShortcut>G + key</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
